import { AnalyticsService } from "../analytics";
import type { PaymentMethod } from "../types";

export { generateCheckoutRequestId, getOrCreateCheckoutRequestId, withRequestId } from "./checkout-request-id";
export { centeredPopupFeatures } from "./popup-window";
export { validateFormFields } from "./validate-form-fields";

export const $ = (selector: string) => {
  const ele = document.querySelector(selector);
  if (!ele) {
    throw new Error(`Cannot find element with selector: ${selector}`);
  }
  return ele;
};

export const definePaymentMethod = <TExternalFuncs, TName extends string>(
  paymentMethod: PaymentMethod<TExternalFuncs, TName>,
) => paymentMethod;

export const createCheckoutIFrame = (type: string, baseUrl: string, params?: Record<string, string>) => {
  const iframe = document.createElement("iframe");

  const searchParams = new URLSearchParams(params);
  const searchParamsStr = searchParams.toString();

  iframe.src = `${baseUrl}/embeds/v1/${type}${searchParamsStr ? `?${searchParamsStr}` : ""}`;

  Object.assign(iframe.style, { width: "100%", height: "100%", border: "none" });

  return iframe;
};

/**
 * Collects browser and client information for fraud detection.
 *
 * Shared by every payment method (card, bnpl, paypal, apple-pay, google-pay), so it
 * must stay general-purpose fraud telemetry — do NOT narrow it to any one processor's
 * shape. VGS 3DS has its own field set; the card flow overrides `browserInfo` with
 * `collect3dsBrowserInfo()` on the card path only (see card.ts). See `collect3dsBrowserInfo`.
 */
export function collectFraudMetadata() {
  return {
    ipAddress: undefined, // IP address should be collected server-side
    browserInfo: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      screenHeight: window.screen.height,
      screenWidth: window.screen.width,
      colorDepth: window.screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    processorFraudInfo: undefined,
  };
}

/**
 * `FraudMetadata` as the backend model spells it on the wire (snake_case outer
 * keys; the keys inside stay camelCase and are read as sent).
 */
export type FraudMetadataWire = {
  // The non-card subset: ip_address is filled server-side, and the 3DS fields
  // (three_ds_browser_info / three_ds_origin / three_ds_return_url) belong to
  // the card flow, which sends them through the generated SDK.
  browser_info?: ReturnType<typeof collectFraudMetadata>["browserInfo"];
  processor_fraud_info?: Record<string, unknown>;
};

/**
 * Per-page state of the processors' fraud fingerprint scripts, keyed by the
 * script element id `index.ts` computes. `loaded` comes only from the script's
 * own onload / onerror (never from the element being in the DOM); `refs` counts
 * the live PaymentKit instances relying on it, so a cleanup() of one instance
 * on a multi-instance page does not disarm the others.
 */
type FraudScriptState = { loaded: boolean; refs: number; ready: Promise<void> };
const fraudScripts = new Map<string, FraudScriptState>();
const withheldDeviceIds = new Set<string>();
export const AIRWALLEX_FRAUD_SCRIPT_ID = "airwallex-fraud-api";
// True once a session on this page listed Airwallex's fingerprint script, from
// the moment the URLs are known (before the script loads). Withholding the id
// is only worth reporting when a fingerprint was expected at all.
let airwallexScriptExpected = false;
// The settle promise a bounded wait gave up on. While it is pending we cannot
// know whether this page expects an Airwallex fingerprint (the session is not
// in yet), so a withheld id is reported only once it resolves, and only if the
// session did list the script. A timeout alone never implies "expected": a slow
// session fetch on a page with no Airwallex processor must stay silent.
let timedOutSettle: Promise<void> | undefined;

export function markAirwallexScriptExpected(): void {
  airwallexScriptExpected = true;
}

/**
 * Called by index.ts cleanup() of an instance whose session listed the script.
 * Without it the flag is monotonic: on an SPA page, a later instance with no
 * Airwallex processor would report a "missing" fingerprint nobody expected. A
 * still-live Airwallex instance keeps its script record, which is evidence enough.
 */
export function clearAirwallexScriptExpected(): void {
  airwallexScriptExpected = false;
}

/** Called by index.ts when an instance injects, or adopts an already-present, script. */
export function adoptFraudScript(scriptId: string, ready: Promise<void>): FraudScriptState {
  const existing = fraudScripts.get(scriptId);
  if (existing) {
    existing.refs += 1;
    return existing;
  }
  const state: FraudScriptState = { loaded: false, refs: 1, ready };
  fraudScripts.set(scriptId, state);
  return state;
}

/** Called by index.ts from the script's own onload / onerror. */
export function markFraudScriptLoaded(scriptId: string, loaded: boolean): void {
  const state = fraudScripts.get(scriptId);
  if (state) state.loaded = loaded;
}

/**
 * Called by index.ts cleanup(): drops this instance's reference; the record and
 * the script are only forgotten once no live instance relies on them. Returns
 * true when the caller should remove the element.
 */
export function releaseFraudScript(scriptId: string): boolean {
  const state = fraudScripts.get(scriptId);
  if (!state) return false;
  state.refs -= 1;
  if (state.refs > 0) return false;
  fraudScripts.delete(scriptId);
  return true;
}

export function fraudScriptReady(scriptId: string): Promise<void> | undefined {
  return fraudScripts.get(scriptId)?.ready;
}

// Longest a card submit waits for the fingerprint script to settle. The script is small and injected at init; this only matters for a
// shopper who submits within the first moments of the page.
const FINGERPRINT_SETTLE_TIMEOUT_MS = 1000;

/**
 * Wait, bounded, for the fraud fingerprint scripts to have loaded or failed, so
 * the device id decision is deterministic instead of a race with `onload`.
 * Never on the tap-to-sheet path (PAY-1538): only the card submit, which is
 * already awaiting init promises, calls it. Wallets never wait.
 */
export async function awaitFingerprintSettled(ready: Promise<void> | undefined): Promise<void> {
  if (!ready) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const settled = await Promise.race([
    ready.then(() => true),
    new Promise<boolean>((resolve) => {
      timer = setTimeout(() => resolve(false), FINGERPRINT_SETTLE_TIMEOUT_MS);
    }),
  ]);
  clearTimeout(timer);
  if (!settled) timedOutSettle = ready;
}

function reportWithheldDeviceId(checkoutRequestId: string): void {
  if (withheldDeviceIds.has(checkoutRequestId)) return;
  withheldDeviceIds.add(checkoutRequestId);
  console.warn("[PaymentKit] Airwallex fingerprint script not loaded — sending no device id");
  try {
    AnalyticsService.capture("airwallex_fingerprint_missing", { request_id: checkoutRequestId });
  } catch {
    // analytics must never break checkout
  }
}

function airwallexFingerprintExpected(): boolean {
  return (
    fraudScripts.has(AIRWALLEX_FRAUD_SCRIPT_ID) ||
    airwallexScriptExpected ||
    !!(typeof document !== "undefined" && document.getElementById(AIRWALLEX_FRAUD_SCRIPT_ID))
  );
}

/**
 * The Airwallex `device_id` to put on a request, or undefined when the
 * fingerprint script has not (yet) loaded on this page. Shared by the card path
 * and every hand-built body so a card and a wallet attempt on the same page
 * never disagree about the device id. A withheld id is reported once per
 * checkout request so the silent case stays measurable.
 */
export function airwallexDeviceIdForRequest(checkoutRequestId: string | undefined): string | undefined {
  if (!checkoutRequestId) return undefined;
  const state = fraudScripts.get(AIRWALLEX_FRAUD_SCRIPT_ID);
  if (state?.loaded) return checkoutRequestId;
  // Nothing to report when no fingerprint was ever expected here: no Airwallex
  // processor on the session and no Airwallex script on the page. Every other
  // way of ending up without a loaded script is reported: the script failed or
  // has not loaded, the element was put there by something we did not inject
  // (a merchant preload, another bundle). When the bounded wait gave up before
  // the session was in, the answer is not known yet: decide once it is.
  if (airwallexFingerprintExpected()) {
    reportWithheldDeviceId(checkoutRequestId);
  } else if (timedOutSettle) {
    void timedOutSettle.then(() => {
      if (airwallexFingerprintExpected()) reportWithheldDeviceId(checkoutRequestId);
    });
  }
  return undefined;
}

/**
 * The fraud metadata for a request body built by hand (`JSON.stringify`), as
 * opposed to one sent through the generated SDK: the SDK's outbound schema renames
 * `browserInfo` / `processorFraudInfo` to the backend's `browser_info` /
 * `processor_fraud_info`; a hand-built body has to spell them out itself. Until
 * Sept 2026 the wallet, PayPal, BNPL, SEPA and Link bodies sent the camelCase keys
 * and the backend (which ignores unknown keys) dropped the telemetry.
 *
 * `checkoutRequestId` is the `data-order-session-id` the Airwallex fingerprint
 * script was loaded with (see `index.ts`); it goes out as `airwallexDeviceId`,
 * the way `card.ts` sends it, but only once that script has loaded.
 */
export function toFraudMetadataWire(checkoutRequestId: string | undefined): FraudMetadataWire {
  const { browserInfo } = collectFraudMetadata();
  const airwallexDeviceId = airwallexDeviceIdForRequest(checkoutRequestId);
  return {
    browser_info: browserInfo,
    // airwallexFingerprintCollected: this PaymentKit.js saw the script's own
    // onload (older versions sent the id unconditionally on the card path).
    processor_fraud_info: airwallexDeviceId ? { airwallexDeviceId, airwallexFingerprintCollected: true } : undefined,
  };
}

/**
 * Collects the browser fingerprint fields for card 3DS — serves BOTH VGS
 * `authenticate` and native Adyen 3DS2 (channel: web).
 *
 * The card flow sends this ONE object as `fraudMetadata.threeDsBrowserInfo`; the
 * backend routes to whichever 3DS mechanism the account's processor uses and each
 * reads only the keys it needs:
 *  - VGS `authenticate` wants six snake_case sub-fields — java_enabled /
 *    javascript_enabled (strings) + color_depth / screen_height / screen_width / tz
 *    (integers). `VgsCmpClient._snake_case_keys` maps our camelCase keys and passes
 *    any others through unchanged, so the extra Adyen keys below (and the
 *    server-filled `acceptHeader`) ride along harmlessly.
 *  - Native Adyen 3DS2 `browserInfo` (web) additionally requires `userAgent`,
 *    `language`, and `timeZoneOffset` (UTC−local, signed minutes — the same value
 *    as `tz`); `acceptHeader` is filled server-side. `adyen_domain.to_adyen_browser_info`
 *    maps exactly the keys it needs and ignores the rest.
 *
 * The six VGS fields keep their exact names/types/string-encoding so VGS is not
 * disturbed. Kept SEPARATE from `collectFraudMetadata()` so neither field set leaks
 * into the other's blob. Used only by the card checkout flow.
 */
export function collect3dsBrowserInfo() {
  return {
    // --- VGS six-field set (names/types unchanged) ---
    javaEnabled: "false",
    javascriptEnabled: "true",
    colorDepth: window.screen.colorDepth,
    screenHeight: window.screen.height,
    screenWidth: window.screen.width,
    tz: new Date().getTimezoneOffset(), // integer minutes (VGS `tz`, not an IANA string)
    // --- Native Adyen 3DS2 web-required additions (acceptHeader is server-filled) ---
    userAgent: navigator.userAgent,
    language: navigator.language,
    timeZoneOffset: new Date().getTimezoneOffset(), // signed minutes; same source as `tz`
  };
}

type AddressInput = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
};

type AddressOutput = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
};

/**
 * Maps address fields from snake_case to camelCase format.
 */
export function mapAddress(address?: AddressInput): AddressOutput | undefined {
  if (!address) return undefined;

  return {
    line1: address.line1,
    line2: address.line2,
    city: address.city,
    state: address.state,
    zipCode: address.zip_code,
    country: address.country,
  };
}
